import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import styles from './SearchableSelect.module.css';

interface Option {
    id: string | number;
    name: string;
}

interface SearchableSelectProps {
    label: string;
    options: Option[];
    placeholder?: string;
    onChange: (value: string | number | undefined) => void;
    error?: string;
    value?: string | number;
    containerClassName?: string;
    inputClassName?: string;
    labelClassName?: string;
    dropdownClassName?: string;
    hideSearchIcon?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    label,
    options,
    placeholder = "Search...",
    onChange,
    error,
    value,
    containerClassName,
    inputClassName,
    labelClassName,
    dropdownClassName,
    hideSearchIcon
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedName, setSelectedName] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const selectedOption = options.find(opt => opt.id === value);
        if (selectedOption) {
            setSelectedName(selectedOption.name);
            setSearchQuery(selectedOption.name);
        } else {
            setSelectedName('');
            setSearchQuery('');
        }
    }, [value, options]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // Reset search query to selected name if we click outside
                setSearchQuery(selectedName);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedName]);

    const filteredOptions = options.filter(option =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (option.id && String(option.id).includes(searchQuery))
    );

    const handleSelect = (option: Option) => {
        setSelectedName(option.name);
        setSearchQuery(option.name);
        setIsOpen(false);
        onChange(option.id);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (!isOpen) setIsOpen(true);
        if (e.target.value === '') {
            onChange(undefined);
            setSelectedName('');
        }
    };

    return (
        <div className={`${styles.container} ${containerClassName || ''}`} ref={dropdownRef}>
            <div className={styles.labelRow}>
                <span className={`${styles.labelText} ${labelClassName || ''}`}>{label}</span>
            </div>
            <div className={styles.inputWrapper}>
                {!hideSearchIcon && (
                    <div className={styles.searchIcon}>
                        <FontAwesomeIcon icon={faSearch} />
                    </div>
                )}
                <input
                    type="text"
                    className={`${styles.input} ${error ? styles.error : ''} ${!hideSearchIcon ? styles.hasIcon : styles.hasRightIconOnly} ${inputClassName || ''}`}
                    placeholder={placeholder}
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                />
                <div className={styles.chevronIcon} onClick={() => setIsOpen(!isOpen)}>
                    <FontAwesomeIcon icon={faChevronDown} className={isOpen ? styles.rotate : ''} />
                </div>
                {searchQuery && (
                    <div className={styles.clearIcon} onClick={() => {
                        setSearchQuery('');
                        onChange(undefined);
                        setSelectedName('');
                    }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </div>
                )}
                {isOpen && (
                    <div className={`${styles.dropdown} ${dropdownClassName || ''}`}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.id}
                                    className={styles.option}
                                    onClick={() => handleSelect(option)}
                                >
                                    <span className={styles.optionName}>{option.name}</span>
                                    {option.id && <span className={styles.optionCode}>Code: {option.id}</span>}
                                </div>
                            ))
                        ) : (
                            <div className={styles.noResults}>No schools found</div>
                        )}
                    </div>
                )}
            </div>
            {error && <span className={styles.errorMessage}>{error}</span>}
        </div>
    );
};

export default SearchableSelect;
